import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useModalGate } from '../contexts/ModalGateContext';
import { avisosAPI } from '../services/api';
import AvisoModal from './AvisoModal';

const AvisoHost = () => {
  const { isAuthenticated, selectedContrato } = useAuth();
  const contrato = selectedContrato?.numero ? String(selectedContrato.numero) : '';
  const gate = useModalGate();
  const blocked = gate?.blocked ?? false;
  const gateRef = useRef(gate);
  gateRef.current = gate;

  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [delayAttempt, setDelayAttempt] = useState(0);
  const contratoRef = useRef(contrato);
  contratoRef.current = contrato;
  const readingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !contrato) {
      setQueue([]);
      setCurrent(null);
      setError('');
      setSaving(false);
      readingRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setQueue([]);
    setCurrent(null);
    setError('');
    setSaving(false);
    readingRef.current = false;
    setDelayAttempt(0);

    (async () => {
      try {
        const response = await avisosAPI.pendentes();
        if (cancelled) return;
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setQueue(list);
      } catch (err) {
        if (!cancelled) {
          console.warn('Falha ao buscar avisos:', err);
          setQueue([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, contrato]);

  useEffect(() => {
    if (current || blocked || queue.length === 0) return undefined;
    if (gateRef.current?.isBlocked()) return undefined;

    const aviso = queue[0];
    const seconds = Number(aviso.open_delay_seconds);
    const delayMs = (Number.isFinite(seconds) && seconds >= 1 ? seconds : 4) * 1000;
    const timer = setTimeout(() => {
      if (gateRef.current?.isBlocked()) {
        setDelayAttempt((attempt) => attempt + 1);
        return;
      }
      setCurrent(aviso);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [current, blocked, queue, delayAttempt]);

  const handleRead = async () => {
    if (!current || readingRef.current) return;

    const avisoId = current.id;
    const contratoAtRead = contratoRef.current;
    readingRef.current = true;
    setSaving(true);
    setError('');

    try {
      await avisosAPI.registrarLeitura(avisoId);
      if (contratoRef.current !== contratoAtRead) return;
      setQueue((items) => items.filter((item) => item.id !== avisoId));
      setCurrent(null);
    } catch (err) {
      if (contratoRef.current !== contratoAtRead) return;
      const message = err.response?.data?.message || 'Não foi possível registrar a leitura.';
      setError(message);
      toast.error(message);
    } finally {
      readingRef.current = false;
      if (contratoRef.current === contratoAtRead) setSaving(false);
    }
  };

  return (
    <AvisoModal
      aviso={current}
      isOpen={!!current}
      saving={saving}
      error={error}
      onRead={handleRead}
    />
  );
};

export default AvisoHost;
